/**
 * ECSR-1115 — hide bundle product cards while a Loop Shop Now (on-store exchange) session is
 * active. Paired with loop-shop-now-bundles.css; the theme-card half is pure CSS.
 *
 * 1. Session state. Loop applies `.loop-returns-activated` during an on-store session and
 *    exposes `LoopOnstore.isActive()` for theme scripts. This script mirrors that state onto
 *    <html> as `loop-shop-now` and remembers it for the tab (sessionStorage `loopShopNow`). The
 *    inline script in layout/theme.liquid reads that memory before the first paint, so on
 *    every page after the first one of a session, bundle cards are hidden before Loop's own
 *    script has run.
 *    - Loop's class present, or `isActive()` true: active, straight away.
 *    - Otherwise the bridge is only dropped once Loop has had until 3 seconds after `load` to
 *      report. Dropping it earlier would let a bundle show and then disappear while Loop is
 *      still starting up. The cost: a tab whose session has ended keeps bundles hidden for
 *      those few seconds on its next page.
 *
 * 2. Nosto cards. Nosto renders recommendation slots (`.nosto_element`) and its search and
 *    category grid (`.ns-serp`) from its own templates, so they carry no theme mark. While a
 *    session is active, every product link in those areas is checked by fetching the theme's
 *    own card for it (`/products/<handle>?section_id=pi-fetch-product-card`, which
 *    global.js already uses). That card is rendered by the same Liquid rule as every other
 *    theme card, so "is it a bundle" has one definition. Answers are cached for the tab, and
 *    the check only runs during a session. A Nosto card whose product is a bundle gets
 *    `data-loop-bundle-nosto` on the element that holds that one product. A card whose
 *    product has not answered yet gets `data-loop-bundle-pending` and stays invisible until
 *    it does (see the CSS). Only cards are ever marked, never the Nosto area itself, so
 *    filters, toolbars and cards already checked stay visible when Nosto adds products.
 */
(function () {
  'use strict';

  var ACTIVE_CLASS = 'loop-shop-now';
  var LOOP_CLASS = 'loop-returns-activated';
  var FLAG_KEY = 'loopShopNow';
  var CACHE_KEY = 'loopShopNowBundles';
  var NOSTO_ROOTS = '.nosto_element, .ns-serp';
  var PENDING_ATTR = 'data-loop-bundle-pending';
  var HIDE_ATTR = 'data-loop-bundle-nosto';
  var CARD_SELECTOR = '.ns-product, .nosto-product-info, pi-product-card';
  var BUNDLE_MARK = /data-loop-bundle(?![\w-])/;
  var PRODUCT_PATH = /\/products\/([^\/?#]+)/;
  var SETTLE_MS = 3000;
  var FETCH_TIMEOUT_MS = 5000;
  var MAX_FETCHES = 6;

  var html = document.documentElement;
  var active = html.classList.contains(ACTIVE_CLASS);
  var settled = false;
  var apiPending = false;
  var nostoObserver = null;
  var cache = readCache();
  var inFlight = {};
  var queue = [];
  var running = 0;

  function storageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function storageSet(key, value) {
    try {
      if (value === null) window.sessionStorage.removeItem(key);
      else window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* storage unavailable: the state still applies to this page */
    }
  }

  function readCache() {
    try {
      return JSON.parse(storageGet(CACHE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function loopClassPresent() {
    return html.classList.contains(LOOP_CLASS) || (!!document.body && document.body.classList.contains(LOOP_CLASS));
  }

  function setActive(next) {
    if (next === active) {
      if (next) startNosto();
      return;
    }
    active = next;
    if (next) {
      html.classList.add(ACTIVE_CLASS);
      storageSet(FLAG_KEY, '1');
      startNosto();
    } else {
      html.classList.remove(ACTIVE_CLASS);
      storageSet(FLAG_KEY, null);
      stopNosto();
    }
  }

  function evaluate() {
    if (loopClassPresent()) {
      setActive(true);
      return;
    }
    var loop = window.LoopOnstore;
    if (loop && typeof loop.isActive === 'function') {
      var result;
      try {
        result = loop.isActive();
      } catch (e) {
        result = false;
      }
      if (result && typeof result.then === 'function') {
        if (apiPending) return;
        apiPending = true;
        result.then(
          function (value) {
            apiPending = false;
            if (value) setActive(true);
            else if (settled && !loopClassPresent()) setActive(false);
          },
          function () {
            apiPending = false;
            if (settled && !loopClassPresent()) setActive(false);
          }
        );
        return;
      }
      if (result) {
        setActive(true);
        return;
      }
    }
    if (settled) setActive(false);
  }

  /* ---- Nosto cards ---- */

  function handleOf(href) {
    if (!href) return null;
    var match = PRODUCT_PATH.exec(href);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]).toLowerCase();
    } catch (e) {
      return match[1].toLowerCase();
    }
  }

  function holdsOther(el, handle) {
    var links = el.querySelectorAll('a[href*="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var other = handleOf(links[i].getAttribute('href'));
      if (other && other !== handle) return true;
    }
    return false;
  }

  /*
   * The element that is this product's card inside the Nosto area. Walking up from the link,
   * it never climbs past an element that also holds another product, and it stops at the first
   * real card boundary: the outermost Nosto/theme card wrapper (CARD_SELECTOR) that holds only
   * this product, an `li`, or the direct child of the product-list container (the element
   * whose parent holds other products). If it reaches the Nosto area without meeting any of
   * those (an area with a single product), it falls back to the link itself, so the whole
   * search UI can never be marked.
   */
  function cardFor(link, root, handle) {
    var el = link;
    var card = null;
    while (el !== root && el.parentElement) {
      if (el.matches(CARD_SELECTOR)) card = el;
      else if (el.tagName === 'LI') return el;
      var parent = el.parentElement;
      if (parent === root) return card || (holdsOther(root, handle) ? el : link);
      if (holdsOther(parent, handle)) return card || el;
      el = parent;
    }
    return card || link;
  }

  function scan() {
    if (!active) return;
    var roots = document.querySelectorAll(NOSTO_ROOTS);
    for (var r = 0; r < roots.length; r++) {
      var root = roots[r];
      var links = root.querySelectorAll('a[href*="/products/"]');
      for (var i = 0; i < links.length; i++) {
        var handle = handleOf(links[i].getAttribute('href'));
        if (!handle) continue;
        var card = cardFor(links[i], root, handle);
        var known = cache[handle];
        if (known === undefined) {
          /* Set once and left alone until the answer arrives, so the CSS failsafe timer is
             not restarted by later scans. */
          if (!card.hasAttribute(PENDING_ATTR)) card.setAttribute(PENDING_ATTR, '');
          lookup(handle);
          continue;
        }
        if (known === 1 && !card.hasAttribute(HIDE_ATTR)) card.setAttribute(HIDE_ATTR, '');
        if (card.hasAttribute(PENDING_ATTR)) card.removeAttribute(PENDING_ATTR);
      }
    }
  }

  function lookup(handle) {
    if (inFlight[handle]) return;
    inFlight[handle] = true;
    queue.push(handle);
    pump();
  }

  function pump() {
    while (running < MAX_FETCHES && queue.length) {
      running++;
      check(queue.shift());
    }
  }

  function check(handle) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var url = root + 'products/' + encodeURIComponent(handle) + '?section_id=pi-fetch-product-card';
    var controller = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = controller ? setTimeout(function () { controller.abort(); }, FETCH_TIMEOUT_MS) : null;
    var persist = true;

    fetch(url, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (response) {
        if (!response.ok) return '';
        return response.text();
      })
      .catch(function () {
        /* Network failure or timeout: show the card rather than blank the slot. The answer is
           kept for this page only, so the next page asks again. */
        persist = false;
        return '';
      })
      .then(function (text) {
        if (timer) clearTimeout(timer);
        cache[handle] = BUNDLE_MARK.test(text) ? 1 : 0;
        if (persist) storageSet(CACHE_KEY, JSON.stringify(cache));
        delete inFlight[handle];
        running--;
        scan();
        pump();
      });
  }

  function startNosto() {
    if (nostoObserver || !document.body) return;
    nostoObserver = new MutationObserver(scan);
    nostoObserver.observe(document.body, { childList: true, subtree: true });
    scan();
  }

  function stopNosto() {
    if (nostoObserver) {
      nostoObserver.disconnect();
      nostoObserver = null;
    }
  }

  /* ---- wiring ---- */

  var classObserver = new MutationObserver(evaluate);
  classObserver.observe(html, { attributes: true, attributeFilter: ['class'] });
  if (document.body) classObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });

  var poll = setInterval(function () {
    evaluate();
    if (settled || (window.LoopOnstore && active)) clearInterval(poll);
  }, 250);

  function settle() {
    setTimeout(function () {
      settled = true;
      evaluate();
    }, SETTLE_MS);
  }

  if (document.readyState === 'complete') settle();
  else window.addEventListener('load', settle, { once: true });

  if (active) startNosto();
  evaluate();
})();
